import { ArrowRight, BookOpenText, Calculator, Download, FileSpreadsheet, FileText, Newspaper, Scale } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CtaPanel, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink } from '@/components/page/PageKit';
import { TEMPLATE_CATEGORIES, TEMPLATE_FILES, TEMPLATES, TEMPLATES_HUB, questionCount, templateUrl } from '@/data/templates';
import { useSEO } from '@/hooks/useSEO';

const URL = templateUrl();
const FILE_GROUPS = ['Screening', 'Interviewing', 'Clients', 'Metrics', 'Buying software'] as const;

const itemList = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Free recruitment templates',
  itemListElement: [
    ...TEMPLATES.map(t => ({ url: templateUrl(t.slug), name: `${t.name} template`, description: t.description })),
    ...TEMPLATE_FILES.map(f => ({ url: `https://beatview.ai${f.file}`, name: f.title, description: f.body })),
  ].map((item, i) => ({ '@type': 'ListItem', position: i + 1, url: item.url, name: item.name, description: item.description })),
};

export default function Templates() {
  useSEO({ title: TEMPLATES_HUB.title, description: TEMPLATES_HUB.description, canonicalUrl: URL });
  return (
    <PageShell>
      <PageStructuredData name="Free recruitment templates" description={TEMPLATES_HUB.description} url={URL} type="CollectionPage" breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: 'Templates' }]} />
      <StructuredData data={itemList} />
      <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: 'Templates' }]} eyebrow="Free templates" title={<>Free recruitment templates <span className="bv-hero-highlight">for every stage of a hire.</span></>} description="Question sets, rubrics, forms and checklists for screening, interviewing and checking references. Free to download and adapt, with no sign-up.">
        <InShort>{TEMPLATES_HUB.shortAnswer}</InShort>
      </PageHero>

      <Section tone="white" labelledBy="guided-title">
        <SectionHeading id="guided-title" eyebrow="Templates with guides" title="Templates with a full guide" description="Each one explains when to use it, what a strong answer looks like and what to avoid, with the file to download." />
        <div className="mt-12 space-y-12">
          {TEMPLATE_CATEGORIES.map(category => {
            const items = TEMPLATES.filter(t => t.category === category);
            if (!items.length) return null;
            return (
              <div key={category}>
                <h3 className="font-display text-xl font-semibold text-bv-ink">{category}</h3>
                <ul className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((t, i) => {
                    const count = questionCount(t);
                    return (
                      <Reveal key={t.slug} delay={(i % 3) * 0.05}>
                        <li className="h-full">
                          <SmartLink href={`/templates/${t.slug}`} className="group flex h-full flex-col rounded-2xl border border-bv-blue-line bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-bv-blue/40 hover:shadow-bv-card">
                            <div className="flex items-center justify-between gap-3">
                              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue">{t.file.format === 'CSV' ? <FileSpreadsheet className="h-5 w-5" /> : <FileText className="h-5 w-5" />}</span>
                              <span className="rounded-full bg-bv-blue-wash px-2.5 py-1 text-xs font-semibold text-bv-ink2">{count ? `${count} questions · ` : ''}{t.file.format}</span>
                            </div>
                            <h4 className="mt-4 font-display text-lg font-semibold leading-snug text-bv-ink">{t.name}</h4>
                            <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{t.description}</p>
                            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">Open the template <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                          </SmartLink>
                        </li>
                      </Reveal>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>

      <Section tone="white" className="pt-0" labelledBy="roles-cta-title">
        <div className="flex flex-col items-start justify-between gap-4 rounded-3xl border border-bv-blue-line bg-bv-blue-wash/60 p-6 sm:flex-row sm:items-center sm:p-8">
          <div>
            <h2 id="roles-cta-title" className="font-display text-xl font-semibold text-bv-ink">Hiring for a specific role?</h2>
            <p className="mt-1 text-[15px] leading-6 text-bv-ink2">Interview questions and a scorecard for nurses, sales reps, warehouse associates, software engineers and more.</p>
          </div>
          <SmartLink href="/interview-questions" className="inline-flex shrink-0 items-center gap-1 text-[15px] font-semibold text-bv-blue hover:underline">Interview questions by role <ArrowRight className="h-4 w-4" /></SmartLink>
        </div>
      </Section>

      <Section tone="wash" labelledBy="files-title">
        <SectionHeading id="files-title" eyebrow="More free files" title="Checklists, worksheets and trackers" description="Spreadsheet templates that open in Excel, Google Sheets or Numbers. Each one includes its own instructions." />
        <div className="mt-12 space-y-12">
          {FILE_GROUPS.map(group => (
            <div key={group}>
              <h3 className="font-display text-xl font-semibold text-bv-ink">{group}</h3>
              <ul className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {TEMPLATE_FILES.filter(f => f.category === group).map((f, i) => (
                  <Reveal key={f.file} delay={(i % 3) * 0.05}>
                    <li className="flex h-full flex-col rounded-2xl border border-bv-blue-line bg-white p-5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue"><FileSpreadsheet className="h-5 w-5" /></span>
                        <span className="rounded-full bg-bv-blue-wash px-2.5 py-1 text-xs font-semibold text-bv-ink2">{f.format}</span>
                      </div>
                      <h4 className="mt-4 font-display text-lg font-semibold leading-snug text-bv-ink">{f.title}</h4>
                      <p className="mt-2 flex-1 text-[15px] leading-6 text-bv-ink2">{f.body}</p>
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
                        <a href={f.file} download className="inline-flex items-center gap-1 text-bv-blue hover:underline"><Download className="h-3.5 w-3.5" /> Download</a>
                        {f.guide && <SmartLink href={f.guide} className="inline-flex items-center gap-1 text-bv-ink2 hover:text-bv-blue">Read the guide <ArrowRight className="h-3.5 w-3.5" /></SmartLink>}
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinks eyebrow="Keep reading" title="More free resources" tone="base" items={[
        { title: 'Guides for agencies', body: 'Plain-English guides to AI recruiting, screening software and pricing.', href: '/resources', icon: BookOpenText },
        { title: 'Screening cost calculator', body: 'Estimate the hours and cost of first-pass CV screening.', href: '/screening-cost-calculator', icon: Calculator },
        { title: 'Hiring platform comparisons', body: 'Sourced comparisons of AI interview and hiring platforms.', href: '/alternatives', icon: Scale },
        { title: 'Beatview blog', body: 'Practical writing on screening, interviews and AI in agency recruitment.', href: '/blog', icon: Newspaper },
      ]} />
      <CtaPanel title={<>Turn the templates into <span className="bv-hero-highlight">a working first round.</span></>} description="Beatview applies your criteria and structured questions to every candidate and keeps the evidence. The Free plan covers one active job; AI interviews are on Pro." secondary={{ label: 'Book a demo', href: '/contact' }} />
    </PageShell>
  );
}
